// MPEG audio Layer III (MP3) codec analysis.
//
// An MP3 frame holds 1152 samples per channel (576 for MPEG-2/2.5) as one or
// two "granules" of 576 frequency lines each. The encoder splits the sound
// into 32 subbands with a filter bank and then runs an MDCT in each subband.
// Each line is quantized to a small integer and coded with Huffman tables.
//
// This module decodes the bitstream up to the dequantized, stereo-processed
// MDCT lines (the frequency picture the encoder chose to keep). The rest of a
// full decoder (IMDCT and synthesis) lives in mp3synth.ts, used by tests.
import { sampleRateOf } from '../parsers/mp3';
import type { ParsedFile } from '../parsers/types';
import type { BarsView, CodecAnalysis, CodecLane, CodecView, SpectrumColumn, SpectrumView, TextView } from './types';

// ---------------------------------------------------------------------------
// Header helpers

const hVer = (h: number) => (h >>> 19) & 3; // 3 = MPEG-1, 2 = MPEG-2, 0 = MPEG-2.5
const hLayer = (h: number) => (h >>> 17) & 3; // 1 = Layer III
const hProt = (h: number) => (h >>> 16) & 1; // 0 = CRC present
const hMode = (h: number) => (h >>> 6) & 3; // 0 stereo, 1 joint, 2 dual, 3 mono
const hModeExt = (h: number) => (h >>> 4) & 3; // bit 1 = M/S, bit 0 = intensity
const hdr = (b: Uint8Array, o: number) => ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0;
/** Index into the band tables: 44.1, 48, 32, 22.05, 24, 16, 11.025, 12, 8 kHz. */
const srIndex = (h: number) => (hVer(h) === 3 ? 0 : hVer(h) === 2 ? 3 : 6) + ((h >>> 10) & 3);
const sideInfoBytes = (h: number) => (hVer(h) === 3 ? (hMode(h) === 3 ? 17 : 32) : hMode(h) === 3 ? 9 : 17);
/** Byte offset where a frame's own main data starts (after header, CRC and side info). */
const mainStartOf = (b: Uint8Array, start: number) => {
  const h = hdr(b, start);
  return start + 4 + (hProt(h) ? 0 : 2) + sideInfoBytes(h);
};

// ---------------------------------------------------------------------------
// Tables (ISO/IEC 11172-3 and 13818-3)

/** Scalefactor band widths for long blocks (22 bands, 576 lines), per sample rate. */
const BAND_LONG = [
  [4, 4, 4, 4, 4, 4, 6, 6, 8, 8, 10, 12, 16, 20, 24, 28, 34, 42, 50, 54, 76, 158],
  [4, 4, 4, 4, 4, 4, 6, 6, 6, 8, 10, 12, 16, 18, 22, 28, 34, 40, 46, 54, 54, 192],
  [4, 4, 4, 4, 4, 4, 6, 6, 8, 10, 12, 16, 20, 24, 30, 38, 46, 56, 68, 84, 102, 26],
  [6, 6, 6, 6, 6, 6, 8, 10, 12, 14, 16, 20, 24, 28, 32, 38, 46, 52, 60, 68, 58, 54],
  [6, 6, 6, 6, 6, 6, 8, 10, 12, 14, 16, 18, 22, 26, 32, 38, 46, 52, 64, 70, 76, 36],
  [6, 6, 6, 6, 6, 6, 8, 10, 12, 14, 16, 20, 24, 28, 32, 38, 46, 52, 60, 68, 58, 54],
  [6, 6, 6, 6, 6, 6, 8, 10, 12, 14, 16, 20, 24, 28, 32, 38, 46, 52, 60, 68, 58, 54],
  [6, 6, 6, 6, 6, 6, 8, 10, 12, 14, 16, 20, 24, 28, 32, 38, 46, 52, 60, 68, 58, 54],
  [12, 12, 12, 12, 12, 12, 16, 20, 24, 28, 32, 40, 48, 56, 64, 76, 90, 2, 2, 2, 2, 2],
];
/** Scalefactor band widths for short blocks (13 bands, 192 lines per window). */
const BAND_SHORT = [
  [4, 4, 4, 4, 6, 8, 10, 12, 14, 18, 22, 30, 56],
  [4, 4, 4, 4, 6, 6, 10, 12, 14, 16, 20, 26, 66],
  [4, 4, 4, 4, 6, 8, 12, 16, 20, 26, 34, 42, 12],
  [4, 4, 4, 6, 6, 8, 10, 14, 18, 26, 32, 42, 18],
  [4, 4, 4, 6, 8, 10, 12, 14, 18, 24, 32, 44, 12],
  [4, 4, 4, 6, 8, 10, 12, 14, 18, 24, 30, 40, 18],
  [4, 4, 4, 6, 8, 10, 12, 14, 18, 24, 30, 40, 18],
  [4, 4, 4, 6, 8, 10, 12, 14, 18, 24, 30, 40, 18],
  [8, 8, 8, 12, 16, 20, 24, 28, 36, 2, 2, 2, 26],
];
const starts = (w: number[]) => {
  const s = [0];
  for (const x of w) s.push(s[s.length - 1] + x);
  return s;
};
/** Band start lines (with a final entry = total). */
export const LONG_START = BAND_LONG.map(starts);
export const SHORT_START = BAND_SHORT.map(starts);
const PRETAB = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 3, 3, 3, 2, 0];
const SLEN = [
  [0, 0, 0, 0, 3, 1, 1, 1, 2, 2, 2, 3, 3, 3, 4, 4],
  [0, 1, 2, 3, 0, 1, 2, 3, 1, 2, 3, 1, 2, 3, 2, 3],
];
/** MPEG-2 LSF: number of scalefactors in each of 4 groups, by [table][long|short|mixed]. */
const LSF_NSF = [
  [[6, 5, 5, 5], [9, 9, 9, 9], [6, 9, 9, 9]],
  [[6, 5, 7, 3], [9, 9, 12, 6], [6, 9, 12, 6]],
  [[11, 10, 0, 0], [18, 18, 0, 0], [15, 18, 0, 0]],
  [[7, 7, 7, 0], [12, 12, 12, 0], [6, 15, 12, 0]],
  [[6, 6, 6, 3], [12, 9, 9, 6], [6, 12, 9, 6]],
  [[8, 8, 5, 0], [15, 12, 9, 0], [6, 18, 9, 0]],
];

// Huffman code tables 1–24 of ISO 11172-3, Annex B, Table B.7. Each entry is
// one char for the code length ('a' = 1 bit) and then the code in hex. Entries
// are in symbol order: symbol = x * size + y.
const HUFF_SRC: [number, string][] = [
  [2, 'a1c1b1c0'],
  [3, 'a1c2f01c3c1e01e03e02f00'],
  [3, 'b3b2f01c1b1e01e03e02f00'],
  [4, 'a1c2f06g05c3c1f04g04f07f05g07h01g06f01g01h00'],
  [4, 'c7c3e05g01c6b2d3e02d5d4e04f01f03e03f02g00'],
  [6, 'a1c2f0ah13h10i00ac3d3f07g0ag05h03f0be04g0dh11h08i004g0cg0bh12i00fi00bi002g07g06h09i00ei003j001h06h04i005j003j002j000'],
  [6, 'b3c4f06h12h0ci005c5b1d2h10h09h03f07d3f05h0eh07i003h13h11h0fi00di00aj004h0dg05h08i00bj005j001i00ch04i004i001k001k000'],
  [6, 'c7c5e09f0eh0fi007c6c4d5e05f06h07d7d6e08f08g08h05f0fe06f09g0ag05h01g0bf07g09g06h04i001h0eg04h06h02i006i000'],
  [8, 'a1c2f0ah17i023i01ei00cj011c3d3f08g0ch12i015h0ch07f0bf09g0fh15i020j028i013i006g0eg0dh16i022j02ej017i012j007h14h13i021j02fj01bj016j009j003i01fi016j029j01ak015k014j005k003h0eh0di00aj00bj010j006k005k001i009h08i007j008j004k004k002k000'],
  [8, 'b3c4e0ag18h22i021h15i00fc5c3d4f0ah20h11g0bh0ae0be07f0dg12h1ei01fh14h05g19f0bg13i03bh1bj012h0ci005h23h21h1fi03ai01ej010i007j005h1ch1ai020j013j011k00fj008k00eh0eg0cg09h0di00ej009j004j001h0bg04h06i006j006j003j002j000'],
  [8, 'd9c6e10g21h29i027i026i01ac7c5d6e09g17g10h1ah0be11d7e0bf0eg15h1eg0ah07f11e0af0ff0cg12h1ch0eh05g20f0dg16g13h12h10h09i005h28g11h1fh1dh11i00dh04i002h1bg0cg0bh0fh0ai007i004j001i01bh0ch08i00ci006i003i001j000'],
  [16, 'a1d5f0eg15h22i033i02ej047i02aj034k044k034l043l02cm002bm0013c3d4f0cg13h1fh1ai02ci021i01fi018j020j018k01fl023l016l00ef0ff0dg17h24i03bi031j04dj041i01dj028j01ek028k01bl021m002am0010g16g14h25i03di038j04fj049j040j02bk04ck038k025k01al01fm0019m000eh23g10i03ci039j061j04bk072k05bj036k049k037l029l030m0035m0017n0018i03ah1bi032j060j04cj046k05dk054k04dk03al04fk01dm004am0031n0029n0011i02fi02dj04ej04ak073k05ek05ak04fk045l053l047l032m003bm0026n0024n000fj048i022j038k05fk05ck055l05bl05al056l049m004dm0041m0033n002cp002bp002ai02bh14i01ej02cj037k04ek048l057l04el03dl02em0036m0025n001eo0014o0010j035i019j029j025k02ck03bk036m0051l042m004cm0039n0036n0025n0012p0027o000bj023j021j01fk039k02al052l048m0050l02fm003an0037m0015n0016o001ap0026q00016k035j019j017k026l046l03cl033l024m0037m001am0022n0017o001bo000eo0009p0007k022k020k01cl027l031m004bl01em0034n0030n0028o0034o001co0012p0011p0009p0005l02dk015l022m0040m0038m0032n0031n002dn001fn0013n000co000fp000ao0007p0006p0003m0030l017l014m0027m0024m0023o0035n0015n0010q00017o000do000ao0006q00001p0004p0002l010l00fm0011n001bn0019n0014o001dn000bo0011o000cp0010p0008s00001r00001s00000p0001'],
  [16, 'c7dce12g35g2fh4ci07ci06ci059j07bj06ck077k06bk051l07am003fddc5e10f1bg2eg24h3dh33h2ai046i034j053j041j029k03bk024e13e11e0ff18g29g22h3bh30h28i040i032j04ej03ek050k038k021f1df1cf19g2bg27h3fh37i05di04ci03bj05dj048j036k04bk032k01dg34f16g2ag28h43h39i05fi04fi048i039j059j045j031k042k02ek01bh4dg25g23h42h3ah34i05bi04ai03ei030j04fj03fk05ak03ek028l026i07dg20h3ch38h32i05ci04ei041i037j057j047j033k049k033l046l01ei06dh35h31i05ei058i04bi042j07aj05bj049j038j02ak040k02ck015l019i05ah2bh29i04di049i03fi038j05cj04dj042j02fk043k030l035l024l014i047h22i043i03ci03ai031j058j04cj043k06ak047k036k026l027l017l00fj06di035i033i02fj05aj052j03aj039j030k048k039k029k017l01bm003el009j056i02ai028i025j046j040j034j02bk046k037k02ak019l01dl012l00bm000bk076j044i01ej037j032j02ek04ak041k031k027k018k010l016l00dm000em0007k05bj02cj027j026j022k03fk034k02dk01fl034l01cl013l00el008m0009m0003l07bk03ck03ak035k02fk02bk020k016l025l018l011l00cm000fm000al002m0001l047k025k022k01ek01ck014k011l01al015l010l00al006m0008m0006m0002m0000'],
  [16, 'a1d5f0eh2ci04ai03fj06ej05dk0ack095k08al0f2l0e1l0c3m0178i011c3d4f0cg14h23i03ei035i02fj053j04bj044k077l0c9k06bl0cfh09f0ff0dg17h26i043i03aj067j05ak0a1j048k07fk075k06el0d1l0cei010h2dg15h27i045i040j072j063j057k09ek08cl0fcl0d4l0c7m0183m016dj01ai04bh24i044i041j073j065k0b3k0a4k09bl108l0f6l0e2m018bm017em016ai009i042h1ei03bi038j066k0b9k0adl109k08el0fdl0e8m0190m0184m017an01bdj010j06fi036i034j064k0b8k0b2k0a0k085l101l0f4l0e4l0d9m0181m016en02cbj00aj062i030j05bj058k0a5k09dk094l105l0f8m0197m018dm0174m017co0379o0374j008j055j054j051k09fk09ck08fl104l0f9m01abm0191m0188m017fn02d7n02c9n02c4j007k09aj04cj049k08dk083l100l0f5m01aam0196m018am0180n02dfm0167n02c6m0160k00bk08bk081j043k07dl0f7l0e9l0e5l0dbm0189n02e7n02e1n02d0o0375o0372n01b7j004l0f3k078k076k073l0e3l0dfm018cn02ean02e6n02e0n02d1n02c8n02c2m00dfn01b4k006l0cal0e0l0del0dal0d8m0185m0182m017dm016co0378n01bbn02c3n01b8n01b5p06c0k004n02ebl0d3l0d2l0d0m0172m017bn02den02d3n02cap06c7o0373o036do036cq00d83o0361k002m0179m0171k066l0bbn02d6n02d2m0166n02c7n02c5o0362p06c6o0367q00d82o0366n01b2k000i00ch0ah07i00bi00aj011j00bj009k00dk00ck00ak007k005k003k001h03'],
  [16, 'dfddf2eg50h92i106i0f8j1b2j1aak29dk28dk289k26dk205l408i058dedce15f26g47h82h7ai0d8i0d1i0c6j147j159j13fj129j117h2af2fe16f29g4ag44h80h78i0ddi0cfi0c2i0b6j154j13bj127k21dg12g51f27g4bg46h86h7dh74i0dci0cci0bei0b2j145j137j125j10fg10h93g48g45h87h7fh76h70i0d2i0c8i0bcj160j143j132j11dk21cg0ei107g42h81h7eh77h72i0d6i0cai0c0i0b4j155j13dj12dj119j106g0ci0f9h7bh79h75h71i0d7i0cei0c3i0b9j15bj14aj134j123j110k208g0aj1b3h73h6fh6di0d3i0cbi0c4i0bbj161j14cj139j12aj11bk213k17dh11j1abi0d4i0d0i0cdi0c9i0c1i0bai0b1i0a9j140j12fj11ej10ck202k179h10j14fi0c7i0c5i0bfi0bdi0b5i0aej14dj141j131j121j113k209k17bk173h0bk29ci0b8i0b7i0b3i0afj158j14bj13aj130j122j115k212k17fk175k16eh0ak28cj15ai0abi0a8i0a4j13ej135j12bj11fj114j107k201k177k170k16ah06k288j142j13cj138j133j12ej124j11cj10dj105k200k178k172k16ck167h04k26cj12cj128j126j120j11aj111j10ak203k17ck176k171k16dk169k165h02l409j118j116j112j10bj108j103k17ek17ak174k16fk16bk168k166k164h00h2bg14g13g11g0fg0dg0bg09g07g06g04h07h05h03h01d3'],
];
/** For table_select 0..31: [index into HUFF_SRC + 1 (0 = no data), linbits]. */
const HUFF_SELECT = [
  [0, 0], [1, 0], [2, 0], [3, 0], [0, 0], [4, 0], [5, 0], [6, 0], [7, 0], [8, 0], [9, 0], [10, 0], [11, 0], [12, 0], [0, 0], [13, 0],
  [14, 1], [14, 2], [14, 3], [14, 4], [14, 6], [14, 8], [14, 10], [14, 13], [15, 4], [15, 5], [15, 6], [15, 7], [15, 8], [15, 9], [15, 11], [15, 13],
];
/** Count1 table A (quadruples); table B is a plain 4-bit code. Codes and lengths in value order. */
const QUAD_A_CODES = [1, 5, 4, 5, 6, 5, 4, 4, 7, 3, 6, 0, 7, 2, 3, 1];
const QUAD_A_BITS = [1, 4, 4, 5, 4, 6, 5, 6, 4, 5, 5, 6, 5, 6, 6, 6];

// ---------------------------------------------------------------------------
// Bit reader and Huffman decoding

/** MSB-first bit reader. Reads past the end return zero bits. */
export class Bits {
  pos = 0;
  constructor(readonly buf: Uint8Array) {}
  peek(n: number): number {
    const b = this.buf, p = this.pos >> 3;
    const w = ((b[p] << 24) | (b[p + 1] << 16) | (b[p + 2] << 8) | b[p + 3]) << (this.pos & 7);
    return w >>> (32 - n);
  }
  /** Reads n bits, 0 ≤ n ≤ 24. */
  get(n: number): number {
    if (n === 0) return 0;
    const v = this.peek(n);
    this.pos += n;
    return v;
  }
}

interface Huff {
  /** First 8 bits → (symbol << 5 | length) for short codes, or −node for longer ones. */
  lut: Int32Array;
  /** Binary tree: child > 0 is a node index, child ≤ 0 is a leaf holding −symbol. */
  tree: Int32Array;
}
const PEEK = 8;

function buildHuff(codes: { sym: number; len: number; code: number }[]): Huff {
  const tree: number[] = [0, 0];
  for (const { sym, len, code } of codes) {
    let n = 0;
    for (let i = len - 1; i >= 0; i--) {
      const bit = (code >> i) & 1;
      if (i === 0) tree[2 * n + bit] = -sym;
      else {
        if (tree[2 * n + bit] <= 0) {
          tree[2 * n + bit] = tree.length / 2;
          tree.push(0, 0);
        }
        n = tree[2 * n + bit];
      }
    }
  }
  const t = Int32Array.from(tree);
  const lut = new Int32Array(1 << PEEK);
  for (let k = 0; k < lut.length; k++) {
    let n = 0, done = false;
    for (let b = 0; b < PEEK; b++) {
      const c = t[2 * n + ((k >> (PEEK - 1 - b)) & 1)];
      if (c <= 0) {
        lut[k] = (-c << 5) | (b + 1);
        done = true;
        break;
      }
      n = c;
    }
    if (!done) lut[k] = -n;
  }
  return { lut, tree: t };
}

function huffDecode(br: Bits, hf: Huff): number {
  const e = hf.lut[br.peek(PEEK)];
  if (e > 0) {
    br.pos += e & 31;
    return e >> 5;
  }
  br.pos += PEEK;
  const t = hf.tree;
  let n = -e;
  for (;;) {
    const c = t[2 * n + br.get(1)];
    if (c <= 0) return -c;
    n = c;
  }
}

let HUFF: Huff[] | null = null;
let QUAD_A: Huff | null = null;
/** Builds the decode trees on first use. Leaf symbols are x << 4 | y. */
function tables(): Huff[] {
  if (HUFF) return HUFF;
  HUFF = HUFF_SRC.map(([size, s]) => {
    const codes: { sym: number; len: number; code: number }[] = [];
    let p = 0;
    for (let j = 0; p < s.length; j++) {
      const len = s.charCodeAt(p) - 96;
      const w = Math.ceil(len / 4);
      const code = parseInt(s.slice(p + 1, p + 1 + w), 16);
      p += 1 + w;
      codes.push({ sym: (Math.floor(j / size) << 4) | j % size, len, code });
    }
    return buildHuff(codes);
  });
  QUAD_A = buildHuff(QUAD_A_CODES.map((code, sym) => ({ sym, code, len: QUAD_A_BITS[sym] })));
  return HUFF;
}

// ---------------------------------------------------------------------------
// Requantization tables

/** |q|^(4/3) for every value Huffman coding can produce (15 + 13 linbits). */
const POW43 = new Float64Array(8207);
for (let i = 0; i < POW43.length; i++) POW43[i] = Math.pow(i, 4 / 3);
/** 2^(e/4) for e in −400..99 (index e + 400). */
const GAIN_OFF = 400;
const GAIN = new Float64Array(500);
for (let i = 0; i < GAIN.length; i++) GAIN[i] = Math.pow(2, (i - GAIN_OFF) / 4);
/** MPEG-1 intensity stereo: left and right weights for is_pos 0..6. */
const IS1_L: number[] = [], IS1_R: number[] = [];
for (let i = 0; i < 7; i++) {
  const t = Math.tan((i * Math.PI) / 12);
  IS1_L.push(i === 6 ? 1 : t / (1 + t));
  IS1_R.push(i === 6 ? 0 : 1 / (1 + t));
}

// ---------------------------------------------------------------------------
// Side information

export interface GranuleInfo {
  part23: number;
  bigValues: number;
  globalGain: number;
  sfCompress: number;
  /** window_switching_flag: 1 when block_type is not 0 (normal long block). */
  ws: number;
  /** 0 long, 1 start, 2 short, 3 stop. */
  blockType: number;
  mixed: number;
  table: [number, number, number];
  subblockGain: [number, number, number];
  region0: number;
  region1: number;
  preflag: number;
  sfScale: number;
  count1Table: number;
}

export interface SideInfo {
  h: number;
  lsf: boolean;
  sri: number;
  sampleRate: number;
  nch: number;
  ngr: number;
  mode: number;
  ms: boolean;
  is: boolean;
  mainDataBegin: number;
  /** Byte offsets in the file: frame start, own main data start, frame end. */
  start: number;
  mainStart: number;
  end: number;
  scfsi: number[];
  gr: GranuleInfo[][];
}

/** Reads the header and side info of the Layer III frame at [start, end). */
export function readSideInfo(b: Uint8Array, start: number, end: number): SideInfo {
  const h = hdr(b, start);
  const lsf = hVer(h) !== 3;
  const mode = hMode(h), nch = mode === 3 ? 1 : 2, ngr = lsf ? 1 : 2;
  const ext = mode === 1 ? hModeExt(h) : 0;
  const o = start + 4 + (hProt(h) ? 0 : 2);
  const br = new Bits(b.subarray(o, Math.min(end, o + 32) + 4));
  const mainDataBegin = br.get(lsf ? 8 : 9);
  br.get(lsf ? nch : nch === 1 ? 5 : 3);
  const scfsi = [0, 0];
  if (!lsf) for (let ch = 0; ch < nch; ch++) scfsi[ch] = br.get(4);
  const gr: GranuleInfo[][] = [];
  for (let g = 0; g < ngr; g++) {
    const row: GranuleInfo[] = [];
    for (let ch = 0; ch < nch; ch++) {
      const part23 = br.get(12), bigValues = br.get(9), globalGain = br.get(8), sfCompress = br.get(lsf ? 9 : 4), ws = br.get(1);
      let blockType = 0, mixed = 0, region0 = 0, region1 = 0;
      const table: [number, number, number] = [0, 0, 0];
      const subblockGain: [number, number, number] = [0, 0, 0];
      if (ws) {
        blockType = br.get(2);
        mixed = br.get(1);
        table[0] = br.get(5);
        table[1] = br.get(5);
        for (let w = 0; w < 3; w++) subblockGain[w] = br.get(3);
      } else {
        for (let k = 0; k < 3; k++) table[k] = br.get(5);
        region0 = br.get(4);
        region1 = br.get(3);
      }
      const preflag = lsf ? 0 : br.get(1);
      const sfScale = br.get(1), count1Table = br.get(1);
      row.push({ part23, bigValues, globalGain, sfCompress, ws, blockType: ws ? blockType : 0, mixed: ws && blockType === 2 ? mixed : 0, table, subblockGain, region0, region1, preflag, sfScale, count1Table });
    }
    gr.push(row);
  }
  return {
    h, lsf, sri: srIndex(h), sampleRate: sampleRateOf(h), nch, ngr, mode, ms: (ext & 2) !== 0, is: (ext & 1) !== 0,
    mainDataBegin, start, mainStart: Math.min(end, o + sideInfoBytes(h)), end, scfsi, gr,
  };
}

// ---------------------------------------------------------------------------
// Granule decoding

/** Everything decoded for one granule of one channel. Arrays are in bitstream order unless noted. */
export interface ChannelData {
  g: GranuleInfo;
  /** Long scalefactor bands used (22 for long blocks, 0 for short, 8 or 6 for mixed). */
  longEnd: number;
  /** First short scalefactor band (13 = none). */
  shortStart: number;
  /** Scalefactors: longEnd long values, then 3 per short band (window-minor). */
  sf: Uint8Array;
  /** LSF intensity stereo: the "illegal" is_pos value of each scalefactor (255 = none). */
  isIllegal: Uint8Array | null;
  /** Quantized integers from the Huffman decoder. */
  q: Int32Array;
  /** Dequantized lines as stored (Mid/Side stay Mid/Side), before reordering. */
  stored: Float32Array;
  /** Final lines: stereo processed (Left/Right) and short blocks reordered for the IMDCT. */
  xr: Float32Array;
  part2Bits: number;
  /** Bits the decoder consumed (scalefactors + Huffman data). Equals part2_3_length for a clean stream. */
  usedBits: number;
  /** Region boundaries in lines (bitstream order). */
  region1Start: number;
  region2Start: number;
  bigEnd: number;
  count1End: number;
  /** Last nonzero quantized line + 1. */
  nzEnd: number;
  /** A count1 quadruple ran past part2_3_length and was dropped. */
  overrun: boolean;
  /** Lines (bitstream order) rebuilt with intensity stereo; set for the right channel. */
  isMask: Uint8Array | null;
}

export interface FrameData {
  side: SideInfo;
  /** [granule][channel] */
  ch: ChannelData[][];
  /** Bytes of main data taken from earlier frames (= main_data_begin). */
  borrowed: number;
  /** Bytes of this frame's own main data area. */
  ownSize: number;
  /** Total bits of all granules (sum of part2_3_length). */
  totalBits: number;
  /** Bytes of the reservoir that were not available (file starts or is cut). */
  missing: number;
}

function decodeScalefactors(br: Bits, side: SideInfo, g: GranuleInfo, ch: number, gr: number, prev: ChannelData | null, c: ChannelData) {
  const sf = c.sf;
  sf.fill(0);
  if (!side.lsf) {
    const s1 = SLEN[0][g.sfCompress], s2 = SLEN[1][g.sfCompress];
    let j = 0;
    if (g.blockType === 2) {
      const n = g.mixed ? 17 : 18;
      for (let i = 0; i < n; i++) sf[j++] = br.get(s1);
      for (let i = 0; i < 18; i++) sf[j++] = br.get(s2);
    } else {
      const scfsi = gr === 1 ? side.scfsi[ch] : 0;
      for (let k = 0; k < 4; k++) {
        const n = k === 0 ? 6 : 5;
        if (scfsi & (8 >> k) && prev) for (let i = 0; i < n; i++, j++) sf[j] = prev.sf[j];
        else {
          const sl = k < 2 ? s1 : s2;
          for (let i = 0; i < n; i++) sf[j++] = br.get(sl);
        }
      }
    }
    return;
  }
  // MPEG-2 LSF: scalefac_compress packs up to four field widths.
  const slen = [0, 0, 0, 0];
  let tindex: number;
  let sfc = g.sfCompress;
  const expand = (v: number, n1: number, n2: number, n3: number) => {
    if (n3) { slen[3] = v % n3; v = Math.floor(v / n3); }
    if (n2) { slen[2] = v % n2; v = Math.floor(v / n2); }
    slen[1] = v % n1;
    slen[0] = Math.floor(v / n1);
  };
  const isRight = side.is && ch === 1;
  if (isRight) {
    sfc >>= 1;
    if (sfc < 180) { expand(sfc, 6, 6, 0); tindex = 3; }
    else if (sfc < 244) { expand(sfc - 180, 4, 4, 0); tindex = 4; }
    else { expand(sfc - 244, 3, 0, 0); tindex = 5; }
  } else if (sfc < 400) { expand(sfc, 5, 4, 4); tindex = 0; }
  else if (sfc < 500) { expand(sfc - 400, 5, 4, 0); tindex = 1; }
  else { expand(sfc - 500, 3, 0, 0); tindex = 2; g.preflag = 1; }
  const t2 = g.blockType === 2 ? (g.mixed ? 2 : 1) : 0;
  const ill = isRight ? (c.isIllegal ??= new Uint8Array(40)) : null;
  if (ill) ill.fill(255);
  let j = 0;
  for (let k = 0; k < 4; k++) {
    const n = LSF_NSF[tindex][t2][k], sl = slen[k];
    for (let i = 0; i < n; i++, j++) {
      sf[j] = br.get(sl);
      if (ill && sl) ill[j] = (1 << sl) - 1;
    }
  }
}

function decodeChannel(br: Bits, side: SideInfo, gr: number, ch: number, prev: ChannelData | null, huff: Huff[]): ChannelData {
  const g = side.gr[gr][ch];
  const sri = side.sri;
  let longEnd = 22, shortStart = 13;
  if (g.blockType === 2) {
    if (g.mixed) { longEnd = sri <= 2 ? 8 : 6; shortStart = 3; }
    else { longEnd = 0; shortStart = 0; }
  }
  const c: ChannelData = {
    g, longEnd, shortStart, sf: new Uint8Array(40), isIllegal: null, q: new Int32Array(576), stored: new Float32Array(576), xr: new Float32Array(576),
    part2Bits: 0, usedBits: 0, region1Start: 0, region2Start: 0, bigEnd: 0, count1End: 0, nzEnd: 0, overrun: false, isMask: null,
  };
  const start = br.pos, end = start + g.part23;
  decodeScalefactors(br, side, g, ch, gr, prev, c);
  c.part2Bits = br.pos - start;

  // Big values: pairs coded with up to three tables over three regions.
  const L = LONG_START[sri];
  const bigEnd = Math.min(g.bigValues * 2, 576);
  let r1: number, r2: number;
  if (g.ws) {
    // Region 0 ends at short band 3 (short blocks) or long band 8 (start/stop).
    // Note: ffmpeg uses 36 for short blocks at 8 kHz too; LAME, mpglib and the
    // standard use 72 there.
    r1 = g.blockType === 2 ? 3 * SHORT_START[sri][3] : L[8];
    r2 = 576;
  } else {
    r1 = L[g.region0 + 1];
    r2 = L[Math.min(22, g.region0 + g.region1 + 2)];
  }
  r1 = Math.min(r1, bigEnd);
  r2 = Math.min(Math.max(r2, r1), bigEnd);
  c.region1Start = r1;
  c.region2Start = r2;
  c.bigEnd = bigEnd;
  const q = c.q;
  let k = 0;
  for (let r = 0; r < 3; r++) {
    const rEnd = r === 0 ? r1 : r === 1 ? r2 : bigEnd;
    const [ti, linbits] = HUFF_SELECT[g.table[r]];
    if (ti === 0) { k = rEnd; continue; }
    const hf = huff[ti - 1];
    for (; k < rEnd; k += 2) {
      const v = huffDecode(br, hf);
      let x = v >> 4, y = v & 15;
      if (linbits && x === 15) x += br.get(linbits);
      if (x && br.get(1)) x = -x;
      if (linbits && y === 15) y += br.get(linbits);
      if (y && br.get(1)) y = -y;
      q[k] = x;
      q[k + 1] = y;
    }
  }
  // Count1: quadruples of −1, 0 or +1, until the granule's bits run out.
  const quadA = QUAD_A!;
  while (k <= 572 && br.pos < end) {
    const p0 = br.pos;
    const v = g.count1Table ? 15 - br.get(4) : huffDecode(br, quadA);
    for (let i = 0; i < 4; i++) {
      let x = (v >> (3 - i)) & 1;
      if (x && br.get(1)) x = -1;
      q[k + i] = x;
    }
    if (br.pos > end) {
      // Some encoders let the last quadruple run past the end. Decoders drop it.
      q[k] = q[k + 1] = q[k + 2] = q[k + 3] = 0;
      br.pos = p0;
      c.overrun = true;
      break;
    }
    k += 4;
  }
  c.count1End = k;
  c.usedBits = br.pos - start;
  br.pos = end;
  let nz = k;
  while (nz > 0 && q[nz - 1] === 0) nz--;
  c.nzEnd = nz;
  dequantize(c, sri);
  return c;
}

function dequantize(c: ChannelData, sri: number) {
  const { g, q, stored, sf, nzEnd } = c;
  const gain = g.globalGain - 210 + GAIN_OFF;
  const shift = g.sfScale + 1;
  const L = LONG_START[sri];
  let k = 0;
  for (let b = 0; b < c.longEnd && k < nzEnd; b++) {
    const e = gain - ((sf[b] + (g.preflag ? PRETAB[b] : 0)) << shift);
    const m = GAIN[e < 0 ? 0 : e];
    for (const kEnd = L[b + 1]; k < kEnd; k++) {
      const v = q[k];
      if (v) stored[k] = v > 0 ? POW43[v] * m : -POW43[-v] * m;
    }
  }
  if (c.shortStart < 13) {
    const S = SHORT_START[sri];
    k = 3 * S[c.shortStart];
    let j = c.longEnd;
    for (let b = c.shortStart; b < 13 && k < nzEnd; b++) {
      const len = BAND_SHORT[sri][b];
      for (let w = 0; w < 3; w++) {
        const e = gain - 8 * g.subblockGain[w] - (sf[j++] << shift);
        const m = GAIN[e < 0 ? 0 : e];
        for (let i = 0; i < len; i++, k++) {
          const v = q[k];
          if (v) stored[k] = v > 0 ? POW43[v] * m : -POW43[-v] * m;
        }
      }
    }
  }
}

/** Joint stereo: M/S and intensity stereo, on bitstream-order lines. Writes Left/Right into l and r. */
function jointStereo(side: SideInfo, c0: ChannelData, c1: ChannelData, l: Float32Array, r: Float32Array) {
  l.set(c0.stored);
  r.set(c1.stored);
  const ms = side.ms;
  const msBand = (p: number, len: number) => {
    if (!ms) return;
    for (let j = p; j < p + len; j++) {
      const m = l[j], s = r[j];
      l[j] = (m + s) * Math.SQRT1_2;
      r[j] = (m - s) * Math.SQRT1_2;
    }
  };
  if (!side.is) {
    msBand(0, 576);
    return;
  }
  // Intensity stereo: above the highest nonzero line of the right channel,
  // the right channel stores only a direction (is_pos) per band.
  const mask = (c1.isMask = new Uint8Array(576));
  const sri = side.sri, sf = c1.sf;
  const lsfTab = side.lsf ? c1.g.sfCompress & 1 : 0;
  const legal = (idx: number) => {
    const v = sf[idx];
    return side.lsf ? c1.isIllegal?.[idx] !== v : v < 7;
  };
  const isBand = (p: number, len: number, idx: number) => {
    const v = sf[idx];
    let kl: number, kr: number;
    if (!side.lsf) { kl = IS1_L[v]; kr = IS1_R[v]; }
    else {
      const f = Math.pow(2, (-(lsfTab + 1) * ((v + 1) >> 1)) / 4);
      if (v & 1) { kl = f; kr = 1; } else { kl = 1; kr = f; }
    }
    for (let j = p; j < p + len; j++) {
      const x = l[j];
      l[j] = x * kl;
      r[j] = x * kr;
      mask[j] = 1;
    }
  };
  const nonzero = (p: number, len: number) => {
    for (let j = p; j < p + len; j++) if (c1.q[j] !== 0) return true;
    return false;
  };
  let p = 576;
  const nzShort = [false, false, false];
  if (c1.shortStart < 13) {
    for (let b = 12; b >= c1.shortStart; b--) {
      const len = BAND_SHORT[sri][b];
      const sfb = b === 12 ? 11 : b; // the last band has no scalefactor: use the one below
      for (let w = 2; w >= 0; w--) {
        p -= len;
        if (!nzShort[w] && nonzero(p, len)) nzShort[w] = true;
        if (!nzShort[w] && sfb >= c1.shortStart && legal(c1.longEnd + 3 * (sfb - c1.shortStart) + w)) isBand(p, len, c1.longEnd + 3 * (sfb - c1.shortStart) + w);
        else msBand(p, len);
      }
    }
  }
  let nzFound = nzShort[0] || nzShort[1] || nzShort[2];
  for (let b = c1.longEnd - 1; b >= 0; b--) {
    const len = BAND_LONG[sri][b];
    p -= len;
    if (!nzFound && nonzero(p, len)) nzFound = true;
    const sfb = b === 21 ? 20 : b;
    if (!nzFound && legal(sfb)) isBand(p, len, sfb);
    else msBand(p, len);
  }
}

/** Short blocks: from bitstream order (band, window, line) to IMDCT order (band, line, window). */
function reorder(src: Float32Array, dst: Float32Array, c: ChannelData, sri: number) {
  if (c.shortStart >= 13) { dst.set(src); return; }
  const S = SHORT_START[sri];
  const s0 = 3 * S[c.shortStart];
  for (let i = 0; i < s0; i++) dst[i] = src[i];
  for (let b = c.shortStart; b < 13; b++) {
    const len = BAND_SHORT[sri][b], base = 3 * S[b];
    for (let j = 0; j < len; j++) for (let w = 0; w < 3; w++) dst[base + 3 * j + w] = src[base + w * len + j];
  }
}

// ---------------------------------------------------------------------------
// Frame decoder over a parsed file

export class Mp3Decoder {
  readonly starts: number[];
  readonly ends: number[];
  private scratch = new Uint8Array(4096);

  constructor(readonly bytes: Uint8Array, frames: { start: number; end: number }[]) {
    this.starts = frames.map((f) => f.start);
    this.ends = frames.map((f) => f.end);
    tables();
  }
  get length(): number {
    return this.starts.length;
  }
  side(i: number): SideInfo {
    return readSideInfo(this.bytes, this.starts[i], this.ends[i]);
  }

  /**
   * Collects the main data of frame i: main_data_begin bytes from the end of
   * earlier frames' main data areas, then this frame's own area. Before the
   * first audio frame we read the raw bytes (the Xing/Info frame's area).
   */
  private gather(i: number, side: SideInfo): { buf: Uint8Array; missing: number } {
    const b = this.bytes;
    const own = side.end - side.mainStart;
    const need = side.mainDataBegin;
    const size = need + own + 8;
    if (this.scratch.length < size) this.scratch = new Uint8Array(size * 2);
    const buf = this.scratch;
    let left = need, dst = need;
    for (let j = i - 1; j >= 0 && left > 0; j--) {
      const ms = mainStartOf(b, this.starts[j]), e = this.ends[j];
      const take = Math.min(left, Math.max(0, e - ms));
      buf.set(b.subarray(e - take, e), dst - take);
      dst -= take;
      left -= take;
    }
    if (left > 0) {
      const s0 = this.starts[0];
      const take = Math.min(left, s0);
      buf.set(b.subarray(s0 - take, s0), dst - take);
      dst -= take;
      left -= take;
      if (left > 0) buf.fill(0, 0, left);
    }
    buf.set(b.subarray(side.mainStart, side.end), need);
    buf.fill(0, need + own, need + own + 8);
    return { buf, missing: left };
  }

  decode(i: number): FrameData {
    const huff = tables();
    const side = this.side(i);
    const { buf, missing } = this.gather(i, side);
    const br = new Bits(buf);
    const ch: ChannelData[][] = [];
    let totalBits = 0;
    for (let gr = 0; gr < side.ngr; gr++) {
      const row: ChannelData[] = [];
      for (let c = 0; c < side.nch; c++) {
        row.push(decodeChannel(br, side, gr, c, gr > 0 ? ch[gr - 1][c] : null, huff));
        totalBits += side.gr[gr][c].part23;
      }
      if (side.nch === 2 && side.mode === 1 && (side.ms || side.is)) {
        const l = new Float32Array(576), r = new Float32Array(576);
        jointStereo(side, row[0], row[1], l, r);
        reorder(l, row[0].xr, row[0], side.sri);
        reorder(r, row[1].xr, row[1], side.sri);
      } else for (const c of row) reorder(c.stored, c.xr, c, side.sri);
      ch.push(row);
    }
    return { side, ch, borrowed: side.mainDataBegin, ownSize: side.end - side.mainStart, totalBits, missing };
  }
}

// ---------------------------------------------------------------------------
// Presentation helpers

/** How "short" each window value is, for picking one value per frame. */
const WINDOW_RANK = [0, 2, 4, 1, 3];
const windowValue = (g: GranuleInfo) => (g.blockType === 2 ? (g.mixed ? 4 : 2) : g.blockType);

const LANES: CodecLane[] = [
  {
    name: 'Window',
    help: 'How long a slice of time each MDCT covers. Long windows give fine frequency detail. At a sharp attack, the encoder switches to three short windows, so the quantization noise stays close to the attack and does not spread as "pre-echo" into the quiet moment before it.',
    values: [
      { value: 'Long', help: 'One long window: 576 frequency lines over the whole granule. Best for steady sounds.' },
      { value: 'Start', help: 'A long window with a steep right edge. It prepares for short windows in the next granule.' },
      { value: 'Short', help: 'Three short windows of 192 lines each. Sharp timing, coarse frequency: used at attacks to stop pre-echo.' },
      { value: 'Stop', help: 'A long window with a steep left edge. It returns from short windows to long ones.' },
      { value: 'Mixed', help: 'Long windows for the lowest two subbands and short windows above them. Rare.' },
    ],
  },
  {
    name: 'Stereo',
    help: 'How the two channels are stored. When left and right are similar, the encoder stores their sum (Mid) and difference (Side). The Side channel is then quiet and cheap.',
    values: [
      { value: 'L/R', help: 'Left and right are stored as two separate channels.' },
      { value: 'M/S', help: 'Mid = (L + R)/√2 and Side = (L − R)/√2. Good when the channels are similar.' },
      { value: 'Intensity', help: 'Above some frequency, only one channel is stored, plus a left/right direction per band. It saves bits at low bitrates but loses the fine stereo image.' },
      { value: 'M/S + intensity', help: 'M/S for the low frequencies and intensity stereo for the high ones.' },
    ],
  },
];

const kHz = (hz: number) => `${(hz / 1000).toFixed(1)} kHz`;
const num = (n: number) => n.toLocaleString('en-US');
const WINDOW_NAMES = ['long', 'start', 'short', 'stop', 'mixed'];

type RegionKind = 'coded' | 'small' | 'zero' | 'intensity';
/** Region code per line: 0..2 big-values regions, 3 count1, 4 zero, 5 intensity. */
function regionCodes(c: ChannelData): Uint8Array {
  const a = new Uint8Array(576);
  for (let k = 0; k < 576; k++) {
    a[k] = k < c.region1Start ? 0 : k < c.region2Start ? 1 : k < c.bigEnd ? 2 : k < c.count1End ? 3 : 4;
    if (c.isMask?.[k]) a[k] = 5;
  }
  return a;
}
function regionLabel(c: ChannelData, code: number): { label: string; kind: RegionKind } {
  if (code <= 2) {
    const t = c.g.table[code];
    return HUFF_SELECT[t][0] === 0
      ? { label: `big values · table 0 (all zero, no bits)`, kind: 'zero' }
      : { label: `big values region ${code} · table ${t}${HUFF_SELECT[t][1] ? ` (+${HUFF_SELECT[t][1]} linbits)` : ''}`, kind: 'coded' };
  }
  if (code === 3) return { label: `count1 · table ${c.g.count1Table ? 'B' : 'A'} (values −1, 0, +1)`, kind: 'small' };
  if (code === 4) return { label: 'zero · no bits spent', kind: 'zero' };
  return { label: 'intensity stereo · rebuilt from the left channel', kind: 'intensity' };
}
function runs(codes: ArrayLike<number>, c: ChannelData): SpectrumView['regions'] {
  const out: NonNullable<SpectrumView['regions']> = [];
  let s = 0;
  for (let k = 1; k <= codes.length; k++) {
    if (k === codes.length || codes[k] !== codes[s]) {
      out.push({ start: s, end: k, ...regionLabel(c, codes[s]) });
      s = k;
    }
  }
  return out;
}

function channelNames(side: SideInfo): string[] {
  if (side.nch === 1) return ['Mono'];
  return side.mode === 1 && side.ms ? ['Mid', 'Side'] : ['Left', 'Right'];
}

function tablesUsed(c: ChannelData): string {
  const t: number[] = [];
  const bounds = [c.region1Start, c.region2Start, c.bigEnd];
  let s = 0;
  for (let r = 0; r < 3; r++) {
    if (bounds[r] > s && HUFF_SELECT[c.g.table[r]][0] && !t.includes(c.g.table[r])) t.push(c.g.table[r]);
    s = bounds[r];
  }
  return t.length ? t.join(', ') : 'none';
}

/** SpectrumViews for one granule/channel. */
function spectrumViews(fd: FrameData, gr: number, chIdx: number): SpectrumView[] {
  const { side } = fd;
  const c = fd.ch[gr][chIdx];
  const g = c.g, sri = side.sri, nyq = side.sampleRate / 2;
  const names = channelNames(side);
  const title = `Granule ${gr + 1} · ${names[chIdx]}`;
  const codes = regionCodes(c);
  const msNote = side.mode === 1 && side.ms && side.nch === 2
    ? ` This is the ${names[chIdx]} channel: the decoder rebuilds L = (M + S)/√2 and R = (M − S)/√2.`
    : '';
  if (c.shortStart >= 13) {
    const L = LONG_START[sri];
    const bands = [];
    for (let b = 0; b < 22; b++) {
      if (L[b] >= 576) break;
      bands.push({ start: L[b], end: Math.min(576, L[b + 1]), label: b === 21 ? 'no scalefactor' : `scalefactor ${c.sf[b]}${g.preflag && PRETAB[b] ? ` + ${PRETAB[b]} (preemphasis)` : ''}` });
    }
    const lead = g.blockType === 0
      ? 'Long block: one MDCT turns the granule into 576 frequency lines.'
      : g.blockType === 1
        ? 'Start block: a long MDCT whose window ends steeply, to hand over to short windows next.'
        : 'Stop block: a long MDCT whose window starts steeply, after short windows.';
    const zero = c.count1End < 576 ? ` The encoder spent no bits above ${kHz((c.count1End * nyq) / 576)}.` : '';
    return [{
      kind: 'spectrum', title: `${title} · ${WINDOW_NAMES[g.blockType]}`, nyquist: nyq, coeffs: c.stored, bits: g.part23, bands, regions: runs(codes, c),
      caption: `${lead} ${tablesUsed(c) === 'none' ? 'There are no big values: every line is −1, 0 or +1.' : `Big values use Huffman tables ${tablesUsed(c)}.`}${zero}${msNote}`,
    }];
  }
  if (c.longEnd > 0) {
    // Mixed block: one view in IMDCT order.
    return [{
      kind: 'spectrum', title: `${title} · mixed`, nyquist: nyq, coeffs: c.xr, bits: g.part23,
      caption: `Mixed block: the lowest ${LONG_START[sri][c.longEnd]} lines are one long window; above them, 3 short windows are interleaved line by line.${msNote}`,
    }];
  }
  // Short block: three windows of 192 lines.
  const S = SHORT_START[sri];
  const perWin = [new Float32Array(192), new Float32Array(192), new Float32Array(192)];
  const winCodes = [new Uint8Array(192), new Uint8Array(192), new Uint8Array(192)];
  for (let b = 0; b < 13; b++) {
    const len = BAND_SHORT[sri][b], base = 3 * S[b];
    for (let w = 0; w < 3; w++)
      for (let j = 0; j < len; j++) {
        perWin[w][S[b] + j] = c.stored[base + w * len + j];
        winCodes[w][S[b] + j] = codes[base + w * len + j];
      }
  }
  return perWin.map((coeffs, w) => {
    const bands = [];
    for (let b = 0; b < 13; b++) bands.push({ start: S[b], end: S[b + 1], label: b === 12 ? 'no scalefactor' : `scalefactor ${c.sf[3 * b + w]}` });
    let top = 192;
    while (top > 0 && winCodes[w][top - 1] === 4) top--;
    const sbg = g.subblockGain[w] ? ` Subblock gain makes this window ${g.subblockGain[w] * 12} dB quieter.` : '';
    return {
      kind: 'spectrum' as const, title: `${title} · short window ${w + 1} of 3`, nyquist: nyq, coeffs, bits: w === 0 ? g.part23 : undefined, bands, regions: runs(winCodes[w], c),
      caption: `Short window ${w + 1} of 3: 192 lines over one third of the granule. Short windows give sharp timing and coarse frequency, so noise stays near an attack.${top < 192 ? ` No bits above ${kHz((top * nyq) / 192)}.` : ''}${sbg}${msNote}`,
    };
  });
}

/** A short, fixed explainer of what the views show and why MP3 works this way. */
const HOW_TO_READ: TextView = {
  kind: 'text',
  title: 'How MP3 stores sound',
  body: 'An **MDCT** turns each granule of 576 samples into 576 frequency lines. Music puts most of its energy into a few lines, so most others can be coarse or zero. ' +
    'The lines are grouped into **scalefactor bands**: narrow at low frequencies and wide at high ones, like the critical bands of the ear. ' +
    "Each band's scalefactor sets how coarse its steps are, so the encoder can hide the rounding noise under louder sound nearby. " +
    '**Big values** is the low region where lines can be large; they are coded in pairs with one of 32 Huffman tables. ' +
    '**Count1** holds only −1, 0 and +1, coded four at a time. Above it, every line is zero and costs no bits.',
};

function frameText(fd: FrameData, index: number): TextView {
  const { side } = fd;
  const names = channelNames(side);
  const parts: string[] = [];
  parts.push(`This ${side.lsf ? 'MPEG-2' : 'MPEG-1'} frame holds ${side.ngr === 2 ? 'two granules' : 'one granule'} of 576 samples per channel.`);
  for (let gr = 0; gr < side.ngr; gr++) {
    const w = fd.ch[gr].map((c) => windowValue(c.g));
    const same = w.every((x) => x === w[0]);
    const desc = same
      ? `${WINDOW_NAMES[w[0]]} windows${side.nch === 2 ? ' in both channels' : ''}`
      : w.map((x, i) => `${WINDOW_NAMES[x]} in ${names[i]}`).join(', ');
    parts.push(`Granule ${gr + 1} uses ${desc}.`);
  }
  if (fd.ch.some((row) => row.some((c) => c.g.blockType === 2))) parts.push('Short windows usually mean a sharp attack: short MDCTs keep the quantization noise from smearing into the silence before it (pre-echo).');
  if (side.nch === 2) {
    if (side.mode !== 1) parts.push(side.mode === 2 ? 'The file uses dual channel mode: two independent mono channels.' : 'The channels are stored as plain Left and Right.');
    else if (side.ms && side.is) parts.push('The encoder stores **Mid and Side** in the low bands and uses **intensity stereo** in the high bands.');
    else if (side.ms) parts.push('The encoder stores **Mid** (L + R) and **Side** (L − R) because the channels are similar. The Side channel then needs few bits.');
    else if (side.is) parts.push('The encoder uses **intensity stereo** in the high bands: one channel plus a direction per band.');
    else parts.push('Joint stereo is allowed, but this frame stores plain Left and Right.');
  }
  if (fd.borrowed > 0) parts.push(`The audio data starts **${num(fd.borrowed)} bytes back**, in the bit reservoir: earlier frames left spare bytes, and this frame uses them.`);
  else parts.push('The audio data starts in this frame (main_data_begin is 0).');
  if (fd.missing > 0) parts.push(`${num(fd.missing)} of those bytes are not in the file, so this frame cannot be decoded correctly.`);
  const nyq = side.sampleRate / 2;
  let top = 0;
  for (const row of fd.ch) for (const c of row) top = Math.max(top, topLineOf(c, side.sri));
  if (top > 0) parts.push(`The highest nonzero line is at ${kHz((top * nyq) / 576)}. Above it the encoder stored nothing.`);
  const overrun = fd.ch.flat().some((c) => c.overrun);
  if (overrun) parts.push('One granule has a count1 value that runs past its bit budget. Decoders drop it.');
  return { kind: 'text', title: `Frame ${num(index + 1)} decisions`, body: parts.join(' ') };
}

/** Highest nonzero line + 1, in 576-line frequency units (short windows count 3 lines per line). */
export function topLineOf(c: ChannelData, sri: number): number {
  if (c.nzEnd === 0) return 0;
  if (c.shortStart >= 13) return c.nzEnd;
  const S = SHORT_START[sri];
  const k = c.nzEnd - 1;
  if (k < 3 * S[c.shortStart]) return c.nzEnd;
  let b = c.shortStart;
  while (b < 12 && 3 * S[b + 1] <= k) b++;
  const len = BAND_SHORT[sri][b];
  const j = (k - 3 * S[b]) % len;
  return 3 * (S[b] + j + 1);
}

// ---------------------------------------------------------------------------
// The analyzer

export function analyze(pf: ParsedFile, bytes: Uint8Array): CodecAnalysis | null {
  if (pf.format !== 'mp3' || pf.frames.length === 0) return null;
  const f0 = pf.frames[0];
  if (f0.end - f0.start < 4 || hLayer(hdr(bytes, f0.start)) !== 1) return null;
  const dec = new Mp3Decoder(bytes, pf.frames);
  const frames = pf.frames;
  const h0 = hdr(bytes, f0.start);
  const codec = `${['MPEG-2.5', '', 'MPEG-2', 'MPEG-1'][hVer(h0)]} Layer III`;
  const sri0 = srIndex(h0);

  const topLine = (c: ChannelData) => topLineOf(c, sri0);

  const decisions = (i: number): number[] => {
    const s = frames[i];
    if (s.end - s.start < 4) return [-1, -1];
    const side = readSideInfo(bytes, s.start, s.end);
    let win = 0;
    for (const row of side.gr) for (const g of row) {
      const v = windowValue(g);
      if (WINDOW_RANK[v] > WINDOW_RANK[win]) win = v;
    }
    let st = -1;
    if (side.nch === 2) st = side.mode === 1 ? (side.ms ? 1 : 0) + (side.is ? 2 : 0) : 0;
    return [win, st];
  };

  const detail = (i: number): CodecView[] => {
    const fd = dec.decode(i);
    const text = frameText(fd, i);
    const names = channelNames(fd.side);
    const views: CodecView[] = [text, HOW_TO_READ];
    const hdrBits = (fd.side.mainStart - fd.side.start) * 8;
    const frameBits = (fd.side.end - fd.side.start) * 8;
    const bitItems: BarsView['items'] = [{ label: 'Header + side info', value: hdrBits, note: 'Frame header, optional CRC and side info' }];
    fd.ch.forEach((row, gr) => row.forEach((c, ch) => bitItems.push({
      label: `Granule ${gr + 1} · ${names[ch]}`, value: c.g.part23,
      note: `${num(c.part2Bits)} bits of scalefactors + ${num(c.usedBits - c.part2Bits)} bits of Huffman data`,
    })));
    bitItems.push({ label: 'Whole frame', value: frameBits, note: `${num(frameBits / 8)} bytes from sync word to next frame` });
    views.push({
      kind: 'bars', title: 'Bits per granule and channel', unit: 'bits', items: bitItems,
      caption: `Each granule and channel gets its own bit budget (part2_3_length). Loud or complex parts get more. Together they use ${num(fd.totalBits)} bits; the frame itself holds ${num(frameBits)}, so the difference comes from or goes to the bit reservoir.`,
    });
    const usedBytes = Math.ceil(fd.totalBits / 8);
    const fromOwn = Math.max(0, usedBytes - fd.borrowed);
    views.push({
      kind: 'bars', title: 'Bit reservoir', unit: 'bytes',
      items: [
        { label: 'Borrowed from earlier frames', value: fd.borrowed, note: 'main_data_begin: how far back the audio data starts' },
        { label: 'Taken from this frame', value: Math.min(fromOwn, fd.ownSize) },
        { label: 'Left for later frames', value: Math.max(0, fd.ownSize - fromOwn), note: 'Spare bytes a later, harder frame can borrow' },
      ],
      caption: 'Every frame at one bitrate has the same size, but some sounds need more bits than others. The reservoir lets an easy frame leave spare bytes that a hard frame (often one with short windows) uses later.',
    });
    fd.ch.forEach((row, gr) => row.forEach((_, ch) => views.push(...spectrumViews(fd, gr, ch))));
    return views;
  };

  // No extra scaling: with the standard's gain formula, a full-scale sine
  // already peaks near 1.0 in both long and short blocks (measured: 1.11 / 1.05).
  const columns = (i: number): SpectrumColumn[] => {
    const s = frames[i];
    if (!s.time) return [];
    const fd = dec.decode(i);
    if (fd.missing > 0) return [];
    const { side } = fd;
    const nyq = side.sampleRate / 2;
    const [t0, t1] = s.time;
    const dt = (t1 - t0) / side.ngr;
    const out: SpectrumColumn[] = [];
    const inv = 1 / side.nch;
    for (let gr = 0; gr < side.ngr; gr++) {
      const row = fd.ch[gr];
      const g0 = t0 + gr * dt;
      if (row.every((c) => c.shortStart >= 13)) {
        const mags = new Float32Array(576);
        for (const c of row) {
          const x = c.xr;
          for (let k = 0; k < 576; k++) mags[k] += Math.abs(x[k]) * inv;
        }
        out.push({ t0: g0, t1: g0 + dt, mags, nyquist: nyq });
        continue;
      }
      for (let w = 0; w < 3; w++) {
        const mags = new Float32Array(192);
        for (const c of row) {
          const x = c.xr;
          const shortFrom = c.shortStart >= 13 ? 192 : c.longEnd > 0 ? LONG_START[side.sri][c.longEnd] / 3 : 0;
          for (let k = 0; k < 192; k++) {
            let v: number;
            if (k >= shortFrom) v = Math.abs(x[3 * k + w]);
            else {
              const a = x[3 * k], b = x[3 * k + 1], cc = x[3 * k + 2];
              v = Math.sqrt(a * a + b * b + cc * cc);
            }
            mags[k] += v * inv;
          }
        }
        out.push({ t0: g0 + (w * dt) / 3, t1: g0 + ((w + 1) * dt) / 3, mags, nyquist: nyq });
      }
    }
    return out;
  };

  const summarize = () => {
    const notes: { level: 'info' | 'warn'; text: string }[] = [];
    const n = frames.length;
    let shortFrames = 0, msFrames = 0, isFrames = 0, jointFrames = 0, stereo = false;
    for (let i = 0; i < n; i++) {
      const d = decisions(i);
      if (d[0] === 2 || d[0] === 4) shortFrames++;
      if (d[1] >= 0) stereo = true;
      if (d[1] === 1 || d[1] === 3) msFrames++;
      if (d[1] >= 2) isFrames++;
      const s = frames[i];
      if (s.end - s.start >= 4 && hMode(hdr(bytes, s.start)) === 1) jointFrames++;
    }
    const pct = (a: number) => `${((100 * a) / Math.max(1, n)).toFixed(a && a < n / 100 ? 1 : 0)}%`;
    notes.push({
      level: 'info',
      text: shortFrames
        ? `${pct(shortFrames)} of frames use short windows. The encoder picks them at sharp attacks (drum hits, plucks), where a long window would smear noise before the attack (pre-echo).`
        : 'No frame uses short windows. Either the sound has no sharp attacks, or the encoder was told not to switch.',
    });
    if (stereo) {
      if (msFrames) notes.push({ level: 'info', text: `${pct(msFrames)} of frames store Mid and Side instead of Left and Right. This saves bits when the channels are similar.` });
      else if (jointFrames) notes.push({ level: 'info', text: 'The file allows joint stereo, but no frame uses M/S.' });
      else notes.push({ level: 'info', text: 'Every frame stores Left and Right separately (plain stereo). M/S coding could save bits on similar channels.' });
      if (isFrames) notes.push({ level: 'info', text: `${pct(isFrames)} of frames use intensity stereo: above some frequency they keep only one channel and a direction per band.` });
    }
    // Decode a sample of frames (all of them for short files) for the zero statistics.
    const step = Math.max(1, Math.ceil(n / 1500));
    let lines = 0, zeros = 0, top = 0, nyq = 0;
    const tops: number[] = [];
    for (let i = 0; i < n; i += step) {
      let fd: FrameData;
      try { fd = dec.decode(i); } catch { continue; }
      if (fd.missing > 0) continue;
      nyq = fd.side.sampleRate / 2;
      let ft = 0;
      for (const row of fd.ch) for (const c of row) {
        lines += 576;
        for (let k = 0; k < 576; k++) if (c.q[k] === 0) zeros++;
        ft = Math.max(ft, topLine(c));
      }
      tops.push(ft);
      top = Math.max(top, ft);
    }
    if (lines > 0 && nyq > 0) {
      tops.sort((a, b) => a - b);
      const typical = tops[Math.floor(tops.length * 0.9)] ?? top;
      notes.push({
        level: 'info',
        text: `The highest stored frequency is ${kHz((top * nyq) / 576)}${typical < top - 6 ? ` (90% of frames stay below ${kHz((typical * nyq) / 576)})` : ''}. Above it every line is zero: the encoder's lowpass filter removes sounds that would cost many bits and that few people hear.`,
      });
      notes.push({ level: 'info', text: `${((100 * zeros) / lines).toFixed(0)}% of all frequency lines are stored as zero. Zeros are almost free to code, which is where most of the compression comes from.` });
    }
    return notes;
  };

  return { codec, lanes: LANES, decisions, detail, columns, summarize };
}
